// Dev-only mirror of the real SDK's EvisioError class + parseFileFailReason helper.
// Shape must stay 1:1 with evisio2sdk.d.ts so consumers can treat instances of this
// the same way they treat the real one (kind discriminator, optional code/cause/toolName/timeoutMs).

import type { EvisioErrorKind, FileFailReason } from "../../sdk/evisio2sdk";

class MockEvisioError extends Error {
    readonly kind: EvisioErrorKind;
    readonly toolName?: string;
    readonly timeoutMs?: number;
    readonly code?: string;
    readonly evisioCause?: unknown;

    constructor(kind: EvisioErrorKind, message: string, extras?: { toolName?: string; timeoutMs?: number; code?: string; cause?: unknown; }) {
        super(message);
        this.name = "EvisioError";
        this.kind = kind;
        if (extras){
            this.toolName    = extras.toolName;
            this.timeoutMs   = extras.timeoutMs;
            this.code        = extras.code;
            this.evisioCause = extras.cause;
        }
    }

    static aborted(message?: string): MockEvisioError {
        return new MockEvisioError("aborted", message || "aborted");
    }
    static popupBlocked(message?: string): MockEvisioError {
        return new MockEvisioError("popupBlocked", message || "popup blocked");
    }
    static disconnected(message?: string): MockEvisioError {
        return new MockEvisioError("disconnected", message || "disconnected");
    }
    static timeout(toolName: string, timeoutMs: number): MockEvisioError {
        return new MockEvisioError("timeout", "tool \"" + toolName + "\" timed out after " + timeoutMs + "ms", { toolName: toolName, timeoutMs: timeoutMs });
    }
    static handlerThrew(toolName: string, cause: unknown): MockEvisioError {
        return new MockEvisioError("handlerThrew", "tool \"" + toolName + "\" handler threw", { toolName: toolName, cause: cause });
    }
    static notRegistered(toolName: string): MockEvisioError {
        return new MockEvisioError("notRegistered", "tool \"" + toolName + "\" is not registered", { toolName: toolName });
    }
    static fileTooLarge(message?: string): MockEvisioError {
        return new MockEvisioError("fileTooLarge", message || "file too large");
    }
    static fileCleaned(message?: string): MockEvisioError {
        return new MockEvisioError("fileCleaned", message || "file was cleaned");
    }
    static notFound(message?: string): MockEvisioError {
        return new MockEvisioError("notFound", message || "not found");
    }
    static uploadFailed(message?: string, extras?: { code?: string; cause?: unknown; }): MockEvisioError {
        return new MockEvisioError("uploadFailed", message || "upload failed", extras);
    }
    static detachFailed(message?: string, extras?: { code?: string; cause?: unknown; }): MockEvisioError {
        return new MockEvisioError("detachFailed", message || "detach failed", extras);
    }
    static unsupported(message?: string): MockEvisioError {
        return new MockEvisioError("unsupported", message || "unsupported");
    }
}

// Port of the SDK's parseFileFailReason — recognizes the three colon-delimited cap codes
// (too_many_pages, too_many_slides, too_long); everything else (including null/undefined/"")
// becomes { kind: "other", text }. Pure; safe to call on any input.
function parseFileFailReason(reason: string | null | undefined): FileFailReason {
    if (reason == null || reason === "") return { kind: "other", text: "" };

    let m = /^too_many_pages:(\d+):(\d+)$/.exec(reason);
    if (m) return { kind: "tooManyPages", actual: parseInt(m[1], 10), limit: parseInt(m[2], 10) };

    m = /^too_many_slides:(\d+):(\d+)$/.exec(reason);
    if (m) return { kind: "tooManySlides", actual: parseInt(m[1], 10), limit: parseInt(m[2], 10) };

    if (reason === "too_long") return { kind: "tooLong" };

    return { kind: "other", text: reason };
}

export { MockEvisioError, parseFileFailReason };
